import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Send, Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HelpChatMessage } from "./HelpChatMessage";
import { VoiceInputButton } from "@/components/chat/VoiceInputButton";
import { useHelpChat } from "@/hooks/useHelpChat";

interface HelpChatDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function HelpChatDialog({ open, onOpenChange }: HelpChatDialogProps) {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const { messages, isLoading, sendMessage, clearChat } = useHelpChat();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom on new messages. The ScrollArea ref points at the
  // Radix Root (overflow:hidden); scroll the inner Viewport instead.
  useEffect(() => {
    const viewport = scrollRef.current?.querySelector<HTMLElement>(
      "[data-radix-scroll-area-viewport]",
    );
    if (viewport) viewport.scrollTop = viewport.scrollHeight;
  }, [messages]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;
    sendMessage(input.trim());
    setInput("");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] h-[600px] flex flex-col p-0">
        <DialogHeader className="px-6 pt-6 pb-2">
          <div className="flex items-center justify-between">
            <DialogTitle>{t("helpChat.title")}</DialogTitle>
            {messages.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearChat}
                aria-label={t("helpChat.clear")}
                className="h-8 px-2 text-muted-foreground"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </DialogHeader>

        <ScrollArea className="flex-1 px-6" ref={scrollRef}>
          <div className="space-y-4 pb-4">
            {messages.length === 0 && (
              <div className="py-8 space-y-4">
                <p className="text-center text-sm text-muted-foreground">
                  {t("helpChat.placeholder")}
                </p>
                <div className="flex flex-wrap gap-2 justify-center">
                  {(t("helpChat.suggestions", { returnObjects: true }) as string[]).map(
                    (suggestion, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => sendMessage(suggestion)}
                        disabled={isLoading}
                      >
                        {suggestion}
                      </Button>
                    ),
                  )}
                </div>
              </div>
            )}
            {messages.map((msg) => (
              <HelpChatMessage key={msg.id} message={msg} />
            ))}
            {isLoading && (
              <div className="flex gap-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                </div>
                <div className="bg-muted rounded-lg px-3 py-2 text-sm text-muted-foreground">
                  {t("helpChat.thinking")}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <form onSubmit={handleSubmit} className="border-t p-4 flex gap-2">
          <VoiceInputButton
            onTranscript={(text) => setInput((prev) => (prev ? `${prev.trimEnd()} ${text}` : text))}
            disabled={isLoading}
          />
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={t("helpChat.inputPlaceholder")}
            disabled={isLoading}
            autoFocus
          />
          <Button
            type="submit"
            size="icon"
            aria-label={t("aiAssistant.send")}
            disabled={!input.trim() || isLoading}
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
